"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import {
  getVehicleBrands,
  getModelsByBrand,
  getModelYears,
  getVehicleModificationGroups,
} from "@/services/vehicleService";
import { useVehicleSelection } from "@/context/VehicleContext";
import { logger } from "@/utils/logger";
import { normalizeHeroSlides } from "./hero/heroSlides";
import Image from "next/image";

const HeroDisplay = dynamic(
  () => import("./hero/HeroDisplay").then((mod) => mod.HeroDisplay),
);
const HeroFinder = dynamic(
  () => import("./hero/HeroFinder").then((mod) => mod.HeroFinder),
);

const toId = (value) => String(value || "");

const getItemId = (item) =>
  item?._id ||
  item?.id ||
  item?.vehicleId ||
  item?.year ||
  item?.code ||
  item?.slug ||
  "";

const HERO_BACKDROP_SRC =
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=70&w=1600&auto=format&fit=crop";

export const FigmaHeroSection = ({
  slides = [],
  showChips = true,
  showDisplayBanner = true,
  showOverlay = true,
  displayLabel = "Featured Display",
}) => {
  const router = useRouter();
  const { selection, setSelection, clearSelection } = useVehicleSelection();
  const fetchSeqRef = useRef(0);
  const brandsLoadedRef = useRef(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const heroSlides = useMemo(() => normalizeHeroSlides(slides), [slides]);

  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandId, setBrandId] = useState("");

  const loadBrands = useCallback(async () => {
    if (brandsLoadedRef.current || brandsLoading) return;

    setBrandsLoading(true);
    try {
      const data = await getVehicleBrands();
      const normalized = Array.isArray(data) ? data : [];
      setBrands(normalized);
      brandsLoadedRef.current = true;
    } catch (error) {
      logger.error("[FIGMA_HOME] Failed to fetch vehicle brands:", error);
    } finally {
      setBrandsLoading(false);
    }
  }, [brandsLoading]);
  const [modelId, setModelId] = useState("");
  const [yearId, setYearId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [modificationGroups, setModificationGroups] = useState([]);
  const [modificationOptions, setModificationOptions] = useState([]);
  const [loadingState, setLoadingState] = useState({
    models: false,
    years: false,
    variants: false,
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!brandId) return;

    let cancelled = false;
    const loadModels = async () => {
      setLoadingState((prev) => ({ ...prev, models: true }));
      try {
        const data = await getModelsByBrand(brandId);
        if (!cancelled) {
          setModels(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to fetch models:", error);
      } finally {
        if (!cancelled) {
          setLoadingState((prev) => ({ ...prev, models: false }));
        }
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  useEffect(() => {
    if (!brandId || !modelId) return;

    let cancelled = false;
    const loadYears = async () => {
      setLoadingState((prev) => ({ ...prev, years: true }));
      try {
        const data = await getModelYears(modelId, brandId);
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => Number(b.year || 0) - Number(a.year || 0))
          : [];
        if (!cancelled) {
          setYears(sorted);
        }
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to fetch years:", error);
      } finally {
        if (!cancelled) {
          setLoadingState((prev) => ({ ...prev, years: false }));
        }
      }
    };

    loadYears();
    return () => {
      cancelled = true;
    };
  }, [brandId, modelId]);

  const resolveYearId = useCallback((value) => {
    if (!value) return "";
    const match =
      years.find((item) => toId(getItemId(item)) === toId(value)) ||
      years.find((item) => String(item?.year || "") === String(value));
    return match ? String(getItemId(match) || value) : String(value);
  }, [years]);

  useEffect(() => {
    if (!brandId || !modelId || !yearId) return;

    let cancelled = false;
    const loadVariants = async () => {
      const seq = ++fetchSeqRef.current;
      setLoadingState((prev) => ({ ...prev, variants: true }));
      try {
        const groups = await getVehicleModificationGroups({
          brandId,
          modelId,
          yearId,
        });
        const normalizedGroups = Array.isArray(groups) ? groups : [];
        const options = normalizedGroups.flatMap((group) =>
          Array.isArray(group.options) ? group.options : [],
        );
        if (!cancelled && seq === fetchSeqRef.current) {
          setModificationGroups(normalizedGroups);
          setModificationOptions(options);
        }
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to fetch variants:", error);
      } finally {
        if (!cancelled && seq === fetchSeqRef.current) {
          setLoadingState((prev) => ({ ...prev, variants: false }));
        }
      }
    };

    loadVariants();
    return () => {
      cancelled = true;
    };
  }, [brandId, modelId, yearId]);

  const handleBrandChange = useCallback((value) => {
    fetchSeqRef.current += 1;
    setBrandId(value);
    setModelId("");
    setYearId("");
    setVariantId("");
    setModels([]);
    setYears([]);
    setModificationGroups([]);
    setModificationOptions([]);
  }, []);

  const handleModelChange = useCallback((value) => {
    fetchSeqRef.current += 1;
    setModelId(value);
    setYearId("");
    setVariantId("");
    setYears([]);
    setModificationGroups([]);
    setModificationOptions([]);
  }, []);

  const handleYearChange = useCallback((value) => {
    fetchSeqRef.current += 1;
    const resolvedYearId = resolveYearId(value);
    setYearId(resolvedYearId);
    setVariantId("");
    setModificationGroups([]);
    setModificationOptions([]);
  }, [resolveYearId]);

  const handleSearch = useCallback(() => {
    if (!brandId || !modelId || !yearId || !variantId) return;

    const brand = brands.find(
      (item) => toId(item._id || item.id) === toId(brandId),
    );
    const model = models.find(
      (item) => toId(getItemId(item)) === toId(modelId),
    );
    const year = years.find((item) => toId(getItemId(item)) === toId(yearId));
    const vehicle = modificationOptions.find(
      (item) => toId(item.vehicleId || item._id || item.id) === toId(variantId),
    );
    setSelection({
      brandId,
      brandName: brand?.name || "",
      modelId,
      modelName: model?.name || "",
      yearId,
      yearLabel: year?.year ? String(year.year) : "",
      vehicleIds: [variantId],
      vehicleLabels: vehicle
        ? [vehicle.label || buildVariantLabel(vehicle)]
        : [],
    });

    router.push("/categories");
  }, [
    brandId,
    brands,
    modelId,
    models,
    yearId,
    years,
    variantId,
    modificationOptions,
    setSelection,
    router,
  ]);

  const modelPlaceholder = !brandId
    ? "Select Brand"
    : loadingState.models
      ? "Loading Models..."
      : models.length
        ? "Select Model"
        : "No models available";
  const yearPlaceholder = !modelId
    ? "Select Model"
    : loadingState.years
      ? "Loading Years..."
      : years.length
        ? "Select Year"
        : "No years available";
  const variantPlaceholder = !yearId
    ? "Select Year"
    : loadingState.variants
      ? "Loading Variants..."
      : modificationOptions.length
        ? "Select Variant"
        : "No variants available";

  const isSearchEnabled = Boolean(brandId && modelId && yearId && variantId);
  useEffect(() => {
    if (!isDesktop || heroSlides.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [heroSlides.length, isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;
    setActiveSlide((current) =>
      current >= heroSlides.length ? 0 : current,
    );
  }, [heroSlides.length, isDesktop]);

  return (
    <section className="relative min-h-[500px] overflow-hidden bg-[#0b1324] text-white">
      <style>{`
        @media (width: 1024px) {
          .figma-hero-stage {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            gap: 2rem !important;
          }

          .figma-hero-stage > .figma-hero-finder {
            order: 1;
            width: 100%;
            max-width: 760px;
            display: flex;
            justify-content: center;
          }

          .figma-hero-stage > .figma-hero-finder > div {
            width: 100%;
            max-width: 640px;
            margin-left: auto;
            margin-right: auto;
            align-items: center;
            text-align: center;
          }

          .figma-hero-stage > .figma-hero-finder > div .mt-10.rounded-2xl {
            width: 100%;
            max-width: 640px;
            margin-left: auto;
            margin-right: auto;
          }

          .figma-hero-stage > .figma-hero-finder > div > .mt-8.flex.flex-wrap {
            justify-content: center;
          }

          .figma-hero-stage > .figma-hero-display {
            order: 2;
            width: 100%;
            max-width: 760px;
          }
        }
      `}</style>
      {/* Background with dynamic overlay */}
      <div className="absolute inset-0 z-0">
        <Image
          src={HERO_BACKDROP_SRC}
          alt="Car parts backdrop"
          fill
          priority
          fetchPriority="high"
          loading="eager"
          quality={60}
          sizes="(min-width: 1024px) 85vw, 1px"
          className="hidden object-cover lg:block"
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-slate-900/30" />
      </div>

      <div className="figma-hero-stage relative z-10 mx-auto grid w-full max-w-[90rem] gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_1.5fr] lg:px-12 lg:py-24 xl:px-16">
        <div className="figma-hero-finder">
          <div className="block">
            <HeroFinder
              brands={brands}
              brandId={brandId}
              modelId={modelId}
              yearId={yearId}
              variantId={variantId}
              models={models}
              years={years}
              modificationGroups={modificationGroups}
              modificationOptions={modificationOptions}
              loadingState={loadingState}
              brandsLoading={brandsLoading}
              modelPlaceholder={modelPlaceholder}
              yearPlaceholder={yearPlaceholder}
              variantPlaceholder={variantPlaceholder}
              isSearchEnabled={isSearchEnabled}
              onBrandChange={handleBrandChange}
              onBrandDropdownOpen={loadBrands}
              onModelChange={handleModelChange}
              onYearChange={handleYearChange}
              onVariantChange={setVariantId}
              onSearch={handleSearch}
              selection={selection}
              onContinueShopping={() => router.push("/categories")}
              onClearSelection={clearSelection}
            />
          </div>
        </div>

        {isDesktop ? (
          <div className="figma-hero-display">
            <HeroDisplay
              slides={heroSlides}
              activeSlide={activeSlide}
              setActiveSlide={setActiveSlide}
              showChips={showChips}
              showDisplayBanner={showDisplayBanner}
              showOverlay={showOverlay}
              displayLabel={displayLabel}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};
